import React, { Suspense, useEffect } from 'react';
import { Routes, Route, BrowserRouter, useLocation, Navigate } from 'react-router-dom';
import Loader from './components/Loader/Loader';
import ConsentBanner from './components/ConsentBanner/ConsentBanner';
import TechnumOpusLayout from './pages/TechnumOpusLayout';
import { install as installAutoInstrument } from './lib/autoInstrument';
import { useRecordRoute } from './lib/recentlyViewed';

// Pass-2 flag-gated components. Each renders null when its flag is off,
// so importing them up-front is safe and keeps them out of route chunks.
import NewsletterStickyBar from './components/NewsletterStickyBar/NewsletterStickyBar';
import ExitIntentModal from './components/ExitIntentModal/ExitIntentModal';
import A11ySettings from './components/A11ySettings/A11ySettings';
import { ToastProvider } from './components/Toast/Toast';
import ReadingProgressBar from './components/ReadingProgressBar/ReadingProgressBar';
import Redirector from './components/Redirector/Redirector';
import BreadcrumbSchema from './components/BreadcrumbSchema/BreadcrumbSchema';
import SiteSearch from './components/SiteSearch/SiteSearch';

// One-time install of delegated outbound/scroll/time observers. Runs only
// in the browser; safe to import in tests (no-op without window).
installAutoInstrument();

// Lazy load all TechnumOpus pages
// const Homepage = React.lazy(() => import('./Homepage'));
const Homepage = React.lazy(() => import('./pages/Homepage/Homepage'));
const AboutUs = React.lazy(() => import('./pages/AboutUs/AboutUs'));
const Blog = React.lazy(() => import('./pages/Blog/Blog'));
const Careers = React.lazy(() => import('./pages/Careers/Careers'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies/CaseStudies'));
const TridentSapAwsMigration = React.lazy(() => import('./pages/CaseStudies/TridentSapAwsMigration/TridentSapAwsMigration'));
const CloudConsultancyMigration = React.lazy(() => import('./pages/CloudConsultancyMigration/CloudConsultancyMigration'));
const ComputerVisionIntelligence = React.lazy(() => import('./pages/ComputerVisionIntelligence/ComputerVisionIntelligence'));
const Contact = React.lazy(() => import('./pages/Contact/Contact'));
const DigitalTransformation = React.lazy(() => import('./pages/DigitalTransformation/DigitalTransformation'));
const Industries = React.lazy(() => import('./pages/Industries/Industries'));
const Industry40Implementation = React.lazy(() => import('./pages/Industry40Implementation/Industry40Implementation'));
const OnifiedAi = React.lazy(() => import('./pages/OnifiedAi/OnifiedAi'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService/TermsOfService'));
const TechnologyInfrastructureSolutions = React.lazy(() => import('./pages/TechnologyInfrastructureSolutions/TechnologyInfrastructureSolutions'));
const AutomationSolutions = React.lazy(() => import('./pages/AutomationSolutions/AutomationSolutions'));
const MaterialsFinishing = React.lazy(() => import('./pages/Industries/MaterialsFinishing'));
const TextilesApparel = React.lazy(() => import('./pages/Industries/TextilesApparel'));
const ProcessIndustries = React.lazy(() => import('./pages/Industries/ProcessIndustries'));
const PrecisionManufacturing = React.lazy(() => import('./pages/Industries/PrecisionManufacturing'));
const BlogArticle = React.lazy(() => import('./pages/Blog/BlogArticle'));
const AwsPartner = React.lazy(() => import('./pages/AwsPartner/AwsPartner'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));


// Component to handle scroll restoration
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]); // Run effect when pathname changes

  return null; // This component doesn't render anything
}

// Records every route into the recently-viewed store. Must be rendered
// inside the BrowserRouter (uses useLocation).
function RouteRecorder() {
  useRecordRoute();
  return null;
}


const App = () => {
  return (
    <ToastProvider>
    <BrowserRouter>
      <ScrollToTop />
      <Redirector />
      <BreadcrumbSchema />
      <RouteRecorder />
      <ReadingProgressBar />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Wrap all TechnumOpus pages in the TechnumOpusLayout */}       
          <Route element={<TechnumOpusLayout />}>
            <Route index element={<Homepage />} />
            <Route path="about-us" element={<AboutUs />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogArticle />} />
            <Route path="blog1" element={<Navigate to="/blog/data-obesity-epidemic" replace />} />
            <Route path="careers" element={<Careers />} />
            <Route path="case-studies" element={<CaseStudies />} />
            <Route path="case-studies/trident-sap-aws-migration" element={<TridentSapAwsMigration />} />
            <Route path="cloud-migration" element={<CloudConsultancyMigration />} />
            <Route path="computer-vision" element={<ComputerVisionIntelligence />} />
            <Route path="contact" element={<Contact />} />
            <Route path="digital-transformation" element={<DigitalTransformation />} />
            <Route path="industries" element={<Industries />} />
            <Route path="industry-4-0" element={<Industry40Implementation />} />
            <Route path="onified-ai" element={<OnifiedAi />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="technology-infrastructure" element={<TechnologyInfrastructureSolutions />} />
            <Route path="aws-partner" element={<AwsPartner />} />
            <Route path="automation" element={<AutomationSolutions />} />
              <Route path="materials-finishing" element={<MaterialsFinishing />} />
            <Route path="textiles-apparel" element={<TextilesApparel />} />
            <Route path="process-industries" element={<ProcessIndustries />} />
            <Route path="precision-manufacturing" element={<PrecisionManufacturing />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      <ConsentBanner />
      {/*
        Pass-2 features. All three return null unless their flag is on,
        so they ship dormant. Flip via flags.json, ?ff=NAME:1, or
        localStorage 'tn:flags:overrides'.
      */}
      <NewsletterStickyBar />
      <ExitIntentModal />
      <A11ySettings />
      <SiteSearch />
    </BrowserRouter>
    </ToastProvider>

      
  );
};

export default App;
